// Direct-to-S3 upload using the presigned PUT URL from /uploads/init.
// Reports progress via XHR (fetch upload progress is still not portable).

import { api, InitBody } from "./api";
import { sha256File } from "./hash";

export interface UploadProgress {
  phase: "hashing" | "initialising" | "uploading" | "completing" | "done";
  bytesHashed?: number;
  bytesUploaded?: number;
  totalBytes: number;
  evidenceId?: string;
}

export interface UploadOptions {
  file: File;
  visibility: "public" | "private";
  label?: string;
  onProgress?: (p: UploadProgress) => void;
  signal?: AbortSignal;
}

export interface UploadResult {
  evidenceId: string;
  sha256: string;
}

export async function uploadEvidence(opts: UploadOptions): Promise<UploadResult> {
  const { file, visibility, label, onProgress, signal } = opts;
  const total = file.size;

  // 1) Local hash — declaredSha256 gets verified server-side.
  onProgress?.({ phase: "hashing", totalBytes: total, bytesHashed: 0 });
  const declaredSha256 = await sha256File(file, (b, t) =>
    onProgress?.({ phase: "hashing", totalBytes: t, bytesHashed: b }));

  // 2) Init — get presigned PUT URL and evidenceId.
  onProgress?.({ phase: "initialising", totalBytes: total });
  const initBody: InitBody = {
    filename: file.name,
    mimeType: file.type || "application/octet-stream",
    size: file.size,
    visibility,
    declaredSha256,
    ...(label && label.trim() ? { label: label.trim() } : {}),
  };
  const init = await api.initUpload(initBody);

  // 3) PUT to S3 with progress.
  onProgress?.({ phase: "uploading", totalBytes: total, bytesUploaded: 0, evidenceId: init.evidenceId });
  await putWithProgress(init.presignedPutUrl, file, {
    onProgress: bytes => onProgress?.({
      phase: "uploading", totalBytes: total, bytesUploaded: bytes, evidenceId: init.evidenceId,
    }),
    signal,
  });

  // 4) Complete — trigger the finalise state machine.
  onProgress?.({ phase: "completing", totalBytes: total, evidenceId: init.evidenceId });
  await api.completeUpload(init.evidenceId);

  onProgress?.({ phase: "done", totalBytes: total, evidenceId: init.evidenceId });
  return { evidenceId: init.evidenceId, sha256: declaredSha256 };
}

interface PutOptions {
  onProgress?: (bytesUploaded: number) => void;
  signal?: AbortSignal;
}

function putWithProgress(url: string, blob: Blob, opts: PutOptions): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    // Content-Type must match what the server signed — S3 enforces this.
    if (blob.type) xhr.setRequestHeader("Content-Type", blob.type);

    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) opts.onProgress?.(ev.loaded);
    };
    xhr.onerror = () => reject(new Error("network error during upload"));
    xhr.ontimeout = () => reject(new Error("upload timed out"));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`S3 rejected upload (HTTP ${xhr.status})`));
    };

    if (opts.signal) {
      const onAbort = () => { xhr.abort(); reject(new Error("upload aborted")); };
      if (opts.signal.aborted) return onAbort();
      opts.signal.addEventListener("abort", onAbort, { once: true });
    }

    xhr.send(blob);
  });
}
